import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errText } from "../../lib/admin";
import { Btn, Empty, Loading, Pill, Field, field, fmtDate } from "./ui";
import { ImageField } from "./ImageField";
import { TREATMENTS } from "../../lib/treatments";

export const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const toText = (body) => body.map(([t, x]) => (t === "h" ? `## ${x}` : t === "q" ? `> ${x}` : x)).join("\n\n");
const toBlocks = (text) => text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)
  .map((s) => (s.startsWith("## ") ? ["h", s.slice(3)] : s.startsWith("> ") ? ["q", s.slice(2)] : ["p", s]));

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { slug: "", title: "", excerpt: "", category: "", date: today(), read: 4, cover: "", pos: "50% 50%", body: [], published: true, treatment: "" };

const Editor = ({ post, onClose, onSaved }) => {
  const isNew = !post.slug;
  const [f, setF] = useState({ ...post });
  const [text, setText] = useState(toText(post.body || []));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!f.cover) { toast.error("Carica una foto di copertina"); return; }
    const body = { ...f, slug: f.slug || slugify(f.title), read: Number(f.read) || 4, body: toBlocks(text) };
    setSaving(true);
    try {
      const { data } = isNew ? await api.post("/admin/posts", body) : await api.put(`/admin/posts/${post.slug}`, body);
      toast.success(isNew ? "Articolo pubblicato" : "Articolo salvato");
      onSaved(data, post.slug);
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };
  return (
    <form onSubmit={save} className="space-y-5" data-testid="post-editor-form">
      <Field label="Titolo"><input required minLength={3} className={field} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value, slug: isNew ? slugify(e.target.value) : f.slug })} data-testid="post-title-input" /></Field>
      <Field label="Indirizzo della pagina" hint={`/blog/${f.slug || "…"}`}><input required pattern="[a-z0-9]+(-[a-z0-9]+)*" className={field} value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="post-slug-input" /></Field>
      <Field label="Riassunto (anteprima)"><textarea required minLength={3} rows={2} className={field} value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Categoria"><input required className={field} value={f.category} onChange={set("category")} list="post-cats" placeholder="Viso, Corpo…" data-testid="post-category-input" /></Field>
        <Field label="Data"><input required type="date" className={field} value={f.date} onChange={set("date")} data-testid="post-date-input" /></Field>
        <Field label="Minuti di lettura"><input required type="number" min={1} max={60} className={field} value={f.read} onChange={set("read")} data-testid="post-read-input" /></Field>
      </div>
      <Field label="Foto di copertina"><ImageField value={f.cover} onChange={(cover) => setF({ ...f, cover })} testId="post-cover" /></Field>
      <Field label="Pulsante verso una pagina trattamento (facoltativo)">
        <select className={field} value={f.treatment || ""} onChange={set("treatment")} data-testid="post-treatment-select">
          <option value="">Nessuno</option>
          {Object.entries(TREATMENTS).map(([k, t]) => <option key={k} value={k}>{t.it.title}</option>)}
        </select>
      </Field>
      <Field label="Testo" hint="Lascia una riga vuota tra i paragrafi. Inizia con ## per un titoletto, con > per una citazione.">
        <textarea required rows={14} className={`${field} font-[inherit] leading-relaxed`} value={text} onChange={(e) => setText(e.target.value)} data-testid="post-body-input" />
      </Field>
      <label className="flex items-center gap-3 text-sm text-[#14110F] cursor-pointer">
        <input type="checkbox" checked={f.published} onChange={set("published")} className="w-4 h-4 accent-[#8A6A3A]" data-testid="post-published-checkbox" /> Visibile sul sito
      </label>
      <div className="flex flex-wrap gap-3 pt-2">
        <Btn type="submit" loading={saving} data-testid="post-save-btn">{isNew ? "Pubblica articolo" : "Salva modifiche"}</Btn>
        <Btn variant="ghost" onClick={onClose} data-testid="post-cancel-btn">Annulla</Btn>
      </div>
    </form>
  );
};

const Row = ({ p, onEdit, onDelete }) => (
  <article data-testid={`post-row-${p.slug}`} className="bg-white border border-[#e7dfd3] rounded-xl p-3 sm:p-4 flex gap-4 items-center">
    <div className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg overflow-hidden bg-[#efe9df] shrink-0"><img src={p.cover} alt="" className="w-full h-full object-cover" /></div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 mb-1"><Pill tone="gold">{p.category}</Pill>{!p.published && <Pill tone="neutral">Bozza</Pill>}<span className="text-xs text-[#6b625a]">{fmtDate(p.date)}</span></div>
      <h3 className="font-serif-d text-lg sm:text-xl font-light text-[#14110F] leading-snug line-clamp-2">{p.title}</h3>
    </div>
    <div className="flex flex-col sm:flex-row gap-1 shrink-0">
      {p.published && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" aria-label="Apri" className="p-2 text-[#6b625a] hover:text-[#8A6A3A]" data-testid={`post-open-${p.slug}`}><ExternalLink className="w-4 h-4" /></a>}
      <button type="button" aria-label="Modifica" onClick={() => onEdit(p)} className="p-2 text-[#6b625a] hover:text-[#8A6A3A]" data-testid={`post-edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
      <button type="button" aria-label="Elimina" onClick={() => onDelete(p)} className="p-2 text-[#6b625a] hover:text-[#9a3b2b]" data-testid={`post-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  </article>
);

export const PostsAdmin = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.get("/admin/posts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, []);
  const onSaved = (doc, oldSlug) => {
    setItems((xs) => [doc, ...xs.filter((x) => x.slug !== (oldSlug || doc.slug))].sort((a, b) => b.date.localeCompare(a.date)));
    setEditing(null);
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Eliminare l'articolo "${p.title}"?`)) return;
    try { await api.delete(`/admin/posts/${p.slug}`); setItems((xs) => xs.filter((x) => x.slug !== p.slug)); toast.success("Articolo eliminato"); } catch (e) { toast.error(errText(e)); }
  };
  if (!items) return <Loading />;
  return (
    <div className="space-y-6" data-testid="admin-posts">
      <datalist id="post-cats">{Array.from(new Set(items.map((p) => p.category))).map((c) => <option key={c} value={c} />)}</datalist>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[#4f4841]">{items.length} articoli</p>
        <Btn variant="gold" onClick={() => setEditing(EMPTY)} data-testid="post-new-btn"><Plus className="w-4 h-4" /> Nuovo articolo</Btn>
      </div>
      {items.length ? <div className="space-y-3">{items.map((p) => <Row key={p.slug} p={p} onEdit={setEditing} onDelete={onDelete} />)}</div> : <Empty>Nessun articolo.</Empty>}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="bg-[#F6F2EC] max-w-[95vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="post-editor-dialog">
          <DialogTitle className="font-serif-d font-light text-3xl text-[#14110F]">{editing?.slug ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle>
          <DialogDescription className="text-sm text-[#6b625a]">Le modifiche sono visibili sul sito appena salvi.</DialogDescription>
          {editing && <Editor key={editing.slug || "new"} post={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
